/**
 * MyJourneyPage — full-page vertical life timeline.
 *
 * Reuses the EXISTING backend endpoints only — no new API work:
 *   • GET  /api/profile              → profile.life_events, profile.name, profile.birth
 *   • POST /api/profile/life-events  → upsert (acts as create when no id)
 *   • DEL  /api/profile/life-events/:id
 *
 * Renders a clean, Apple-inspired vertical timeline with a thin indigo
 * centre line, 16px markers and alternating 2-column cards on desktop
 * (left of line / right of line). On mobile the layout collapses to a
 * single left-aligned column. Expanding a card reveals the long
 * description (if any). Add/Edit/Delete all route through the EXISTING
 * life-events endpoints.
 *
 * No popups, no modals, no sheets. The compose panel appears INLINE at
 * the top of the timeline.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Plus, Edit3, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/hooks/useAuth";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";

const CATEGORY_LABELS = {
  education: "Education", career: "Career", business: "Business",
  relationship: "Relationships", family: "Family", travel: "Travel",
  milestone: "Milestone", spiritual: "Spiritual",
};

const fmtYear = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})/);
  return m ? m[1] : String(iso);
};

export default function MyJourneyPage() {
  const { user } = useAuth() || {};
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [draft, setDraft] = useState({year: "", title: "", description: "",
                                       category: "milestone"});
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get("/profile");
        if (!dead) {
          const le = (data?.life_events || []).sort(
            (a, b) => String(a.year || "").localeCompare(String(b.year || ""))
          );
          setEvents(le);
        }
      } finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, []);

  const summary = useMemo(() => {
    const name = user?.name?.split(" ")[0] || "friend";
    if (events.length === 0) {
      return `Welcome, ${name}. Every chapter has shaped who you are — add the first one below.`;
    }
    const earliest = events[0]?.year;
    const latest = events[events.length - 1]?.year;
    const span = (earliest && latest && earliest !== latest)
      ? ` from ${fmtYear(earliest)} to ${fmtYear(latest)}`
      : (earliest ? ` since ${fmtYear(earliest)}` : "");
    return `Welcome, ${name}. ${events.length} chapter${events.length === 1 ? "" : "s"}${span}. Every one of them has shaped who you are.`;
  }, [events, user]);

  const resetDraft = () => {
    setDraft({year: "", title: "", description: "", category: "milestone"});
    setEditId(null); setComposeOpen(false);
  };

  const saveEvent = async () => {
    if (!draft.year || !draft.title) return;
    const row = {
      year: draft.year, title: draft.title.slice(0, 80),
      description: (draft.description || "").slice(0, 400),
      category: draft.category,
    };
    try {
      if (editId) await api.post("/profile/life-events", {...row, id: editId});
      else await api.post("/profile/life-events", row);
      const { data } = await api.get("/profile");
      setEvents((data.life_events || []).sort(
        (a, b) => String(a.year || "").localeCompare(String(b.year || ""))));
      resetDraft();
      toast.success(editId ? "Chapter updated." : "Chapter added to your journey.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save.");
    }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/profile/life-events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Chapter removed.");
    } catch (_) {}
  };

  const startEdit = (ev) => {
    setEditId(ev.id);
    setDraft({year: ev.year || "", title: ev.title || "",
               description: ev.description || "",
               category: ev.category || "milestone"});
    setComposeOpen(true);
  };

  return (
    <div data-testid="journey-page"
         style={{minHeight: "100vh", background: "#FAFAFC"}}>
      <KalkiGlobalHeader />
      <main style={{maxWidth: 980, margin: "0 auto",
                     padding: "40px 20px 100px"}}>
        <div style={{textAlign: "center", marginBottom: 36}}>
          <div style={{fontSize: 10, letterSpacing: "0.32em",
                        color: "#5D00FF", marginBottom: 8}}>MY JOURNEY</div>
          <h1 style={{fontSize: 36, fontWeight: 400, letterSpacing: "-0.01em",
                       color: "#1C1C1C", margin: "0 0 10px 0",
                       fontFamily: "'EB Garamond',serif"}}>
            Every chapter has shaped who you are.
          </h1>
          <p data-testid="journey-summary"
             style={{fontSize: 14, color: "#434353", maxWidth: 620,
                      marginInline: "auto", lineHeight: 1.6}}>
            {summary}
          </p>
          <button data-testid="journey-add-btn"
                  onClick={() => { resetDraft();
                                    setDraft({year: String(new Date().getFullYear() - 1),
                                               title: "", description: "",
                                               category: "milestone"});
                                    setComposeOpen(true); }}
                  style={{marginTop: 18,
                           background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                           color: "white", border: 0, padding: "11px 22px",
                           borderRadius: 999, fontSize: 12,
                           letterSpacing: "0.18em", cursor: "pointer"}}>
            <Plus size={13} style={{display: "inline", marginRight: 6,
                                     verticalAlign: "-2px"}} /> ADD CHAPTER
          </button>
        </div>

        {composeOpen && (
          <ComposePanel draft={draft} setDraft={setDraft}
                        onSave={saveEvent} onCancel={resetDraft}
                        isEdit={!!editId} />
        )}

        <div data-testid="journey-timeline"
             style={{position: "relative", marginTop: 40,
                      padding: "20px 0"}}>
          {loading && <p style={{textAlign: "center", color: "#9999A5"}}>
            Loading your journey…</p>}
          {!loading && events.length === 0 && (
            <div data-testid="journey-empty"
                 style={{textAlign: "center", padding: "40px 20px",
                          color: "#676773", lineHeight: 1.6, fontSize: 14}}>
              Your timeline is quiet for now. Add your first chapter above —
              a birth of a career, a move, a loss, a love — and your journey
              will begin to unfold here.
            </div>
          )}
          {events.length > 0 && (
            <>
              <div className="journey-spine"
                   style={{position: "absolute", left: "50%",
                            top: 0, bottom: 0, width: 2,
                            background: "linear-gradient(180deg,rgba(93,0,255,0.1),rgba(93,0,255,0.4) 50%,rgba(93,0,255,0.1))",
                            transform: "translateX(-50%)"}} />
              <div style={{display: "flex", flexDirection: "column", gap: 24}}>
                {events.map((ev, i) => (
                  <TimelineRow key={ev.id || `${ev.year}-${ev.title}`}
                               event={ev} index={i}
                               expanded={expanded[ev.id || i]}
                               onToggle={() => setExpanded((x) => ({
                                 ...x, [ev.id || i]: !x[ev.id || i]}))}
                               onEdit={() => startEdit(ev)}
                               onDelete={() => ev.id && removeEvent(ev.id)} />
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <style>{`
        @media (max-width: 740px) {
          .journey-spine { left: 20px !important; }
          .journey-row { grid-template-columns: 40px 1fr !important; }
          .journey-row .journey-card-wrap { grid-column: 2 !important; justify-self: stretch !important; }
          .journey-row.right .journey-card-wrap,
          .journey-row.left .journey-card-wrap { padding-left: 16px !important; padding-right: 0 !important; }
        }
      `}</style>
    </div>
  );
}

function TimelineRow({ event, index, expanded, onToggle, onEdit, onDelete }) {
  const side = index % 2 === 0 ? "left" : "right";
  return (
    <div className={`journey-row ${side}`}
         data-testid={`journey-event-${event.id || event.year}`}
         style={{display: "grid",
                  gridTemplateColumns: "1fr 40px 1fr",
                  alignItems: "center",
                  columnGap: 0}}>
      <div className="journey-card-wrap"
           style={{
             gridColumn: side === "left" ? "1" : "3",
             justifySelf: side === "left" ? "end" : "start",
             paddingRight: side === "left" ? 20 : 0,
             paddingLeft: side === "right" ? 20 : 0,
             maxWidth: 420, width: "100%",
           }}>
        <EventCard event={event} expanded={expanded}
                   onToggle={onToggle} onEdit={onEdit} onDelete={onDelete} />
      </div>
      <div style={{gridColumn: 2, display: "flex",
                    justifyContent: "center", position: "relative"}}>
        <div data-testid={`journey-marker-${event.id || event.year}`}
             style={{width: 14, height: 14, borderRadius: 999,
                      background: "white", border: "2px solid #5D00FF",
                      boxShadow: "0 0 0 4px rgba(93,0,255,0.08)",
                      zIndex: 2}} />
      </div>
    </div>
  );
}

function EventCard({ event, expanded, onToggle, onEdit, onDelete }) {
  const hasDescription = (event.description || "").length > 0;
  const category = event.category || "milestone";
  return (
    <div style={{background: "white", borderRadius: 18,
                  border: "1px solid rgba(0,0,0,0.05)", padding: 18,
                  boxShadow: "0 4px 16px rgba(20,10,60,0.04)"}}>
      <div style={{display: "flex", alignItems: "center",
                    justifyContent: "space-between", marginBottom: 6}}>
        <div style={{fontSize: 10, letterSpacing: "0.3em",
                      color: "#5D00FF"}}>
          {fmtYear(event.year)} · {CATEGORY_LABELS[category] || "Chapter"}
        </div>
        <div style={{display: "flex", gap: 10}}>
          <button data-testid={`journey-edit-${event.id || event.year}`}
                  onClick={onEdit}
                  style={{background: "none", border: 0, cursor: "pointer",
                           color: "#9999A5"}}>
            <Edit3 size={13} />
          </button>
          <button data-testid={`journey-del-${event.id || event.year}`}
                  onClick={onDelete}
                  style={{background: "none", border: 0, cursor: "pointer",
                           color: "#9999A5"}}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div style={{fontSize: 16, color: "#1C1C1C", fontWeight: 500,
                    fontFamily: "'EB Garamond',serif", lineHeight: 1.3}}>
        {event.title || "A chapter"}
      </div>
      {hasDescription && (
        <>
          <div data-testid={`journey-desc-${event.id || event.year}`}
               style={{fontSize: 13.5, color: "#676773", lineHeight: 1.6,
                        marginTop: 8,
                        maxHeight: expanded ? "none" : 48,
                        overflow: "hidden"}}>
            {event.description}
          </div>
          {event.description.length > 90 && (
            <button data-testid={`journey-toggle-${event.id || event.year}`}
                    onClick={onToggle}
                    style={{marginTop: 6, background: "none", border: 0,
                             color: "#5D00FF", fontSize: 11,
                             letterSpacing: "0.15em", cursor: "pointer",
                             padding: 0, display: "inline-flex",
                             alignItems: "center", gap: 4}}>
              {expanded ? <><ChevronUp size={12} /> SHOW LESS</>
                        : <><ChevronDown size={12} /> READ MORE</>}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ComposePanel({ draft, setDraft, onSave, onCancel, isEdit }) {
  return (
    <div data-testid="journey-compose"
         style={{background: "white", borderRadius: 20, padding: 20,
                  border: "1px solid rgba(93,0,255,0.15)",
                  boxShadow: "0 10px 30px rgba(93,0,255,0.06)",
                  marginBottom: 24}}>
      <div style={{fontSize: 10, letterSpacing: "0.3em",
                    color: "#9999A5", marginBottom: 10}}>
        {isEdit ? "EDIT CHAPTER" : "ADD CHAPTER"}
      </div>
      <div style={{display: "grid", gap: 10,
                    gridTemplateColumns: "90px 1fr", alignItems: "start"}}>
        <input data-testid="journey-year-input"
               value={draft.year}
               onChange={(e) => setDraft((d) => ({...d, year: e.target.value}))}
               placeholder="Year" inputMode="numeric"
               style={{padding: "11px 12px", borderRadius: 12,
                        border: "1px solid rgba(0,0,0,0.08)",
                        fontSize: 14}} />
        <input data-testid="journey-title-input"
               value={draft.title}
               onChange={(e) => setDraft((d) => ({...d, title: e.target.value}))}
               placeholder="A short title — e.g. Started my first business"
               style={{padding: "11px 12px", borderRadius: 12,
                        border: "1px solid rgba(0,0,0,0.08)",
                        fontSize: 14}} />
      </div>
      <select data-testid="journey-category-select"
              value={draft.category}
              onChange={(e) => setDraft((d) => ({...d, category: e.target.value}))}
              style={{marginTop: 10, padding: "11px 12px", borderRadius: 12,
                       border: "1px solid rgba(0,0,0,0.08)",
                       fontSize: 13, width: "100%", background: "white"}}>
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
          <option key={k} value={k}>{v}</option>
        ))}
      </select>
      <textarea data-testid="journey-desc-input"
                value={draft.description}
                onChange={(e) => setDraft((d) => ({...d, description: e.target.value}))}
                rows={3}
                placeholder="One or two sentences — what happened, how it changed you (optional)"
                style={{marginTop: 10, width: "100%", padding: "12px 14px",
                         borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)",
                         fontSize: 14, resize: "vertical",
                         fontFamily: "inherit"}} />
      <div style={{display: "flex", justifyContent: "flex-end", gap: 10,
                    marginTop: 14}}>
        <button data-testid="journey-cancel"
                onClick={onCancel}
                style={{background: "none", border: 0,
                         color: "#9999A5", fontSize: 11,
                         letterSpacing: "0.18em", cursor: "pointer",
                         padding: "11px 18px"}}>CANCEL</button>
        <button data-testid="journey-save"
                onClick={onSave}
                disabled={!draft.year || !draft.title}
                style={{background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                         color: "white", border: 0, padding: "11px 22px",
                         borderRadius: 999, fontSize: 11,
                         letterSpacing: "0.18em", cursor: "pointer",
                         opacity: !draft.year || !draft.title ? 0.4 : 1}}>
          {isEdit ? "SAVE CHANGES" : "ADD TO JOURNEY"}
        </button>
      </div>
    </div>
  );
}
